import './counter-signature.scss'

import { PropType } from "vue";
import Component from "vue-class-component";
import { faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';

import { CounterSignature as CounterSignatureType, CounterSigner as CounterSignerType, SkinDate, createTranslatedString, getTranslatedString } from "@skinsoft/types";

import { ReactiveVue } from "@skinsoft/reactive-vue";
import { FIELD_VALUE_UPDATED } from "@skinsoft/ui2/events";
import { COUNTER_SIGNATURE_UPDATED, COUNTER_SIGNER_UPDATED, REMOVE_COUNTER_SIGNATURE, REMOVE_COUNTER_SIGNER } from "../../../../../busTopics";
import { DATE_FORMATS, buildSkinDateWithFormat, toFormat } from "@skinsoft/utils/date-helper";
import { CounterSigner } from "./counter-signer";
import { TextField, TextArea } from '@skinsoft/ui2';
import { set } from 'lodash';

@Component({
    components: {
        sTextField: TextField,
        sTextArea: TextArea,
        sCounterSigner: CounterSigner,
    },
    props: {
        counterSignature: {
            type: Object as PropType<CounterSignatureType>
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        canBeDeleted: {
            type: Boolean,
        },
        showSigners: {
            type: Boolean,
            default: true,
        }
    },
    template: require('./counter-signature.html')
})
export class CounterSignature extends ReactiveVue {
    private counterSignature: CounterSignatureType
    private lastSignerId: number = 1

    private minusIcon = faTrash
    private plusIcon = faPlusCircle

    private COUNTER_SIGNER_UPDATED = COUNTER_SIGNER_UPDATED
    private REMOVE_COUNTER_SIGNER = REMOVE_COUNTER_SIGNER
    private FIELD_VALUE_UPDATED = FIELD_VALUE_UPDATED

    private onTranslatedStringUpdated(path: string, value: string): CounterSignatureType {
        let updatedSignature: CounterSignatureType = {...this.counterSignature}
        if(updatedSignature[path]?.translatedString) {
            updatedSignature[path].translatedString[this.$i18n.locale] = value
        } else {
            updatedSignature[path] = createTranslatedString(this.$i18n.locale, value)
        }
        return updatedSignature
    }

    private getComment() {
        return getTranslatedString(this.counterSignature.comment, this.$i18n.locale)
    }

    private onCommentUpdated(comment: string) {
        let updatedSignature: CounterSignatureType = this.onTranslatedStringUpdated('comment', comment)
        this.updateCounterSignature(updatedSignature)
    }

    private getLocation() {
        return getTranslatedString(this.counterSignature.location, this.$i18n.locale)
    }

    private onLocationUpdated(location: string) {
        let updatedSignature: CounterSignatureType = this.onTranslatedStringUpdated('location', location)
        this.updateCounterSignature(updatedSignature)
    }

    private getReason() {
        return getTranslatedString(this.counterSignature.reason, this.$i18n.locale)
    }

    private onReasonUpdated(reason: string) {
        let updatedSignature: CounterSignatureType = this.onTranslatedStringUpdated('reason', reason)
        this.updateCounterSignature(updatedSignature)
    }

    public get dateValue(): string {
        if(this.counterSignature.date) {
            return toFormat(this.counterSignature.date as SkinDate, DATE_FORMATS.YYYY_MM_DD_DASH)
        }
        return ""
    }

    public set dateValue(value: string) {
        if(value) {
            let updatedSigner: CounterSignatureType = {...this.counterSignature}
            updatedSigner.date = buildSkinDateWithFormat(value, DATE_FORMATS.YYYY_MM_DD_DASH)
            this.updateCounterSignature(updatedSigner)
        }
    }

    private onCounterSignerUpdated(updatedSigner: CounterSignerType, index: number) {
        if(this.counterSignature.observationCarriedPresenceOfs?.length) {
            let updatedSignature = {...this.counterSignature}
            const signerIndex = updatedSignature.observationCarriedPresenceOfs!.findIndex(signer => {
                return signer.index === index
            })
            if(signerIndex > -1) {
                updatedSignature.observationCarriedPresenceOfs!.splice(signerIndex, 1, updatedSigner)
                this.updateCounterSignature(updatedSignature)
            }
        }
    }

    private onCounterSignerDeleted(index: number) {
        if(this.counterSignature.observationCarriedPresenceOfs?.length) {
            let updatedSignature = {...this.counterSignature}
            updatedSignature.observationCarriedPresenceOfs = updatedSignature.observationCarriedPresenceOfs!.filter(signer => {
                return signer.index !== index
            })
            this.updateCounterSignature(updatedSignature)
        }
    }

    private onAddSigner() {
        let updatedSignature: CounterSignatureType = {...this.counterSignature}
        const blankSigner: CounterSignerType = {
            index: this.lastSignerId,
            surnameFirstName: createTranslatedString(this.$i18n.locale, ""),
            function: createTranslatedString(this.$i18n.locale, ""),
            signature: undefined,
        }
        this.lastSignerId++

        if(updatedSignature.observationCarriedPresenceOfs) {
            updatedSignature.observationCarriedPresenceOfs.push(blankSigner)
        } else {
            set(updatedSignature, 'observationCarriedPresenceOfs', [blankSigner])
        }
        this.updateCounterSignature(updatedSignature)
    }

    private updateCounterSignature(updatedSignature: CounterSignatureType) {
        this.$emit(
            COUNTER_SIGNATURE_UPDATED,
            updatedSignature,
        )
    }

    private removeCounterSignature() {
        this.$emit(
            REMOVE_COUNTER_SIGNATURE,
            this.counterSignature.index
        )
    }

    private getDateFormatOptions() {
        return {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }
    }
}